import React, { useState, useEffect, useMemo } from "react";

// Components
import Table from "./Childs/Table";
import FormCard from "../../Common/Childs/Structure/FormCard";
import Form from "./Childs/Form";
import Delete from "./Delete";

// Common Components
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";

// Importing an icon
import plus from "../../../assets/icons/plus-solid.svg";

// Constants and wallet utility
import constants from "../../../utils/constants";
import { useWallet } from "../../../context/WalletContextProvider";
import { useGlobal } from "../../../context/GlobalContextProvider";
import { useError } from "../../../context/ErrorContextProvider";

// Initial state for the rarity form
const initialState = {
  rarity_type: '',
  starts_on: null,
  ends_on: null,
  max_count: 0
};

const IndexRarity = () => {

  // Destructure context values
  const {
    account,
    connectWallet,
    walletBalance,
    sendTransaction
  } = useWallet();

  const {
    campaign,
    artPack,
    setSpinner,
  } = useGlobal();
  const { showError } = useError();
  const [formError, setFormError] = useState("");
  // State variables
  const [data, setData] = useState([]); // Stores the rarity data
  const [showForm, setShowForm] = useState(false); // Toggles the visibility of the form
  const [formData, setFormData] = useState(initialState); // Stores data for the form

  // Load rarity data from API
  const loadTableData = async () => {
    try {
      setSpinner(true); // Show loading spinner
      const res = await fetch(`/api/campaign/${campaign?.campaign_id}/rarity`);
      if (res.status === 200) {
        const data = await res.json(); // Parse the JSON response
        setData(data); // Update state with fetched data
      }
      setSpinner(false); // Hide loading spinner
    } catch (err) {
      setSpinner(false); // Hide loading spinner on error
      console.error(err); // Log error to console
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    if (campaign) {
      loadTableData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign?.campaign_id]);

  // Opens form for adding a new rarity
  const addNew = () => {
    setShowForm(true);
    setFormData(initialState); // Reset form data
  };

  // Opens form for editing an existing rarity
  const onEdit = (data) => {
    setShowForm(true);
    setFormData(data); // Set form data to the item being edited
  };

  // Prepares to delete an item
  const onDelete = (data) => {
    setShowForm(false);
    setFormData(data); // Set the item to be deleted
  };

  // Discards the form and resets the data
  const onDiscard = () => {
    setShowForm(false);
    setFormData(initialState); // Reset form data
  };

  // Creates a new rarity item
  const createItem = async (data) => {
    setSpinner(true);
    const url = `/api/campaign/${campaign?.campaign_id}/rarity`;
    await fetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    }).then((res) => {
      loadTableData(); // Refresh data after creation
      onDiscard(); // Close form
      setSpinner(false); // Hide spinner
    });
  };

  // Updates an existing rarity item
  const updateItem = async (data) => {
    const { id } = data;
    const url = `/api/campaign/${campaign?.campaign_id}/rarity/${id}`;
    await fetch(url, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    }).then((res) => {
      loadTableData(); // Refresh data after update
      onDiscard(); // Close form
    });
  };

  // Deletes an existing rarity item
  const deleteItem = async () => {
    const { id } = formData; // Get id from formData
    const url = `/api/campaign/${campaign?.campaign_id}/rarity/${id}`;
    await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
      },
    }).then((res) => {
      loadTableData(); // Refresh data after deletion
      onDiscard(); // Close form
    });
  };

  // Handles wallet transfer if balance is insufficient
  const handleTransfer = async (account) => {
    const balance = await walletBalance(account); // Fetch initial wallet balance

    if (Number(balance) < Number(constants.RARIY_BUILD_VALUE)) {
      const required = Number(constants.RARIY_BUILD_VALUE) - Number(balance);
      const tx = await sendTransaction(account, required.toString()); // Initiate transfer
      const receipt = await tx.wait(); // Wait for transaction confirmation
      console.log("Transfer successful:");
      // Function to wait until the balance is greater than the rarity value
      const waitForBalanceUpdate = async () => {
        let updatedBalance;
        do {
          updatedBalance = await walletBalance(account);
          // Add a delay between checks to prevent rapid polling
          await new Promise(resolve => setTimeout(resolve, 2000)); // 1 second delay
        } while (Number(updatedBalance) < Number(constants.RARIY_BUILD_VALUE));

        return updatedBalance;
      };

      return await waitForBalanceUpdate();
    }

    return balance;
  };


  const buildCampaignRarity = async (campaign, deployAddressId) => {
    try {
      const response = await fetch(`/api/build/campaign/${campaign?.campaign_id}/rarity`, {
        method: "POST",
        body: JSON.stringify({
          wid: deployAddressId,
        }),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });

      if (!response.ok) { // Check for HTTP error responses
        const contentType = response.headers.get("content-type");
        let errorMessage;

        // Decide how to parse the error message based on the content type
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          errorMessage = `${errorData?.error}`;
        } else {
          const errorText = await response.text();
          errorMessage = `Error ${response.status}: ${errorText}`;
        }

        setFormError(errorMessage); // Set error message 
        return
      }

      const data = await response.json();
      return data;

    } catch (error) {
      console.error('Fetch error:', error.message);
      // Handle error accordingly
    }
  }


  // Builds rarity and deploys it
  const buildRarity = async () => {
    try {
      setSpinner(true);
      setFormError("");
      const req = await fetch(`/api/wallet/${account}/build/address`);
      const res = await req.json();
      const deployAddressId = res.id; // Get deploy account id
      const balance = await handleTransfer(res.address); // Ensure wallet has enough balance
      if (balance >= constants.RARIY_BUILD_VALUE) {
        await buildCampaignRarity(campaign, deployAddressId); // Fetch campaign rarity
      } else {
        setFormError("Insufficient balance to deploy rarity");
        throw new Error("Insufficient balance to deploy rarity");
      }
      loadTableData(); // Refresh data
      setSpinner(false);
    } catch (error) {
      console.log(error)
      showError("Something went wrong. Please try again")
      setSpinner(false);
    }
  };

  const hasPendingRarity = useMemo(() => {
    return data.find(item => !item.status);
  }, [data])

  // Render form for adding or editing rarity
  if (showForm) {
    return (
      <>
        <Title title="Rarity" />
        <Description text={'Make some traits of your Fund Raisin NFTs rare based on count or dates available for minting.'} />
        <FormCard>
          <Form
            handleSubmit={formData?.id ? updateItem : createItem} // Determine if editing or creating
            formData={formData}
            onDiscard={onDiscard}
            artPack={artPack}
            setSpinner={setSpinner}
            campaign={campaign}
          />
        </FormCard>
      </>
    );
  }

  // Render delete confirmation if needed
  if (!showForm && formData?.id && formData?.delete) {
    return (
      <Delete
        onDiscard={onDiscard}
        handleSubmit={deleteItem}
        formData={formData}
      />
    );
  }

  // Build button for deploying rarity
  const buildButton = () => {
    // Only deployed campaign can be build
    return campaign?.sc_id && hasPendingRarity && (
      <div className="d-flex mb-4 col-sm-12">
        <button className="btn bg-orange form-btn" onClick={() => { (account ? buildRarity() : connectWallet()) }}>
          {account ? 'Deploy Rarity' : 'Connect Wallet'}
        </button>
      </div>
    );
  };

  // Main render
  return (
    <>
      <Title title="Rarity" />
      <Description text={'Make some traits of your Fund Raisin NFTs rare based on count or dates available for minting.'} />

      {formError.length > 0 && (
        <span className="form-text text-danger">
          {formError}
        </span>
      )}
      <div className="add-btn" onClick={addNew}>
        Add Rarity
        <img src={plus} className="add-icon" alt="img" />
      </div>
      <Table
        data={data}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {buildButton()} {/* Render build button */}
    </>
  );
};

export default IndexRarity;
